import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { loadGoogleMaps } from "../components/MapPreview";
import { carIcon } from "../components/gps/carIcon";
import { MapPin, Search, Navigation, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  live: { label: "LIVE", dot: "bg-sky-400", text: "text-sky-300", pin: "#38BDF8" },
  stale: { label: "Posizione non aggiornata", dot: "bg-amber-400", text: "text-amber-300", pin: "#F59E0B" },
  offline: { label: "Telefono offline", dot: "bg-slate-500", text: "text-slate-400", pin: "#64748B" },
  waiting: { label: "In attesa del GPS", dot: "bg-slate-500", text: "text-slate-400" },
  unavailable: { label: "GPS non disponibile", dot: "bg-orange-500", text: "text-orange-300" },
  denied: { label: "Permesso negato", dot: "bg-rose-500", text: "text-rose-300" },
  not_started: { label: "Turno non iniziato", dot: "bg-slate-700", text: "text-slate-500" },
  ended: { label: "Turno terminato", dot: "bg-slate-700", text: "text-slate-500" },
};
const SHIFT = { DAY: "Turno giorno", NIGHT: "Turno notte", SOSTITUZIONE: "Sostituzione" };
const MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#0B1220" }] }, { elementType: "labels.text.fill", stylers: [{ color: "#7C8BA1" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#05080F" }] }, { featureType: "road", elementType: "geometry", stylers: [{ color: "#18243A" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#1F3556" }] }, { featureType: "water", elementType: "geometry", stylers: [{ color: "#040A14" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] }, { featureType: "transit", stylers: [{ visibility: "off" }] },
];
const ago = (iso, now) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.round((now - new Date(iso)) / 1000));
  return s < 60 ? `${s}s fa` : s < 3600 ? `${Math.floor(s / 60)} min fa` : `${Math.floor(s / 3600)} h fa`;
};
const decodePath = (str) => {
  const pts = []; let i = 0, lat = 0, lng = 0;
  while (i < str.length) {
    for (const k of [0, 1]) {
      let r = 0, s = 0, b;
      do { b = str.charCodeAt(i++) - 63; r |= (b & 31) << s; s += 5; } while (b >= 32);
      const d = r & 1 ? ~(r >> 1) : r >> 1;
      if (k === 0) lat += d; else lng += d;
    }
    pts.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return pts;
};
const ROUTE_MSG = { in_service: "Servizio in corso: nessun percorso verso il prossimo ritiro.", no_next: "Nessun prossimo ritiro assegnato.",
  no_position: "Nessuna posizione GPS ricevuta.", no_session: "Turno GPS non attivo.", no_address: "Indirizzo di ritiro mancante.",
  route_not_found: "Percorso non trovato per questo indirizzo.", route_error: "Percorso momentaneamente non disponibile." };

function RouteInfo({ route }) {
  if (!route) return <div className="text-[11px] text-slate-500 pt-1" data-testid="gps-route-loading">Calcolo percorso verso il ritiro…</div>;
  if (!route.available) return <div className="text-[11px] text-slate-500 pt-1" data-testid="gps-route-unavailable">{ROUTE_MSG[route.reason] || "Percorso non disponibile."}</div>;
  return (
    <div className="mt-2 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="gps-route-info">
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-300">Percorso verso il ritiro</div>
      <div className="flex items-baseline gap-3 mt-1">
        <span className="text-xl font-extrabold text-white" data-testid="gps-route-duration">{route.duration_text}</span>
        <span className="text-sm text-slate-300" data-testid="gps-route-distance">{route.distance_text}</span>
      </div>
      <div className="text-[11px] text-slate-400 mt-1">Stima Google Maps con traffico, dalla posizione delle {hhmm(route.from_position_at)}. Ritiro previsto alle {route.booking?.time || "—"}.</div>
    </div>
  );
}

const hhmm = (iso) => iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";

function Counter({ value, label, dot, testid }) {
  return (
    <div className="rounded-xl border border-sky-500/20 bg-[#08111F] px-4 py-2.5 min-w-[120px]" data-testid={testid}>
      <div className="flex items-center gap-2 text-2xl font-extrabold text-white"><span className={`w-2.5 h-2.5 rounded-full ${dot}`} />{value}</div>
      <div className="text-[11px] text-slate-400">{label}</div>
    </div>
  );
}

function DriverRow({ d, sel, onClick, now }) {
  const S = STATUS[d.status] || STATUS.offline;
  return (
    <button type="button" onClick={onClick} data-testid={`gps-driver-${d.driver_id}`}
      className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 transition-colors ${sel ? "bg-sky-500/15 ring-1 ring-sky-500/50" : "hover:bg-white/[0.03]"}`}>
      <div className="w-9 h-9 rounded-full bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-300 text-xs font-bold flex-shrink-0">
        {(d.driver_name || "?").split(" ").map(x => x[0]).slice(0, 2).join("")}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white truncate">{d.driver_name || "Autista"}</div>
        <div className="text-[11px] text-slate-500 font-mono-num">{d.vehicle_plate || "—"}</div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className={`text-[11px] font-bold inline-flex items-center gap-1.5 ${S.text}`} data-testid={`gps-driver-status-${d.driver_id}`}><span className={`w-2 h-2 rounded-full ${S.dot}`} />{S.label}</div>
        <div className="text-[10px] text-slate-500">{d.position ? `Agg. ${ago(d.position.received_at, now)}` : ""}</div>
      </div>
    </button>
  );
}

function BookingLink({ label, b, testid }) {
  const nav = useNavigate();
  if (!b) return <div className="flex justify-between gap-3 py-1.5 border-b border-white/5 text-sm"><span className="text-slate-400">{label}</span><span className="text-slate-500" data-testid={testid}>Nessuno</span></div>;
  return (
    <button type="button" onClick={() => nav(`/prenotazioni?open=${b.id}`)} data-testid={testid}
      className="w-full text-left py-2 border-b border-white/5 hover:bg-sky-500/5 rounded">
      <div className="flex justify-between text-xs"><span className="text-slate-400">{label}</span><span className="text-sky-300 font-bold">{b.booking_number} · {b.status} ›</span></div>
      <div className="text-xs text-slate-200 truncate">{b.date && b.date !== new Date().toISOString().slice(0, 10) ? `${b.date.split("-").reverse().join("/")} ` : ""}{b.time || ""} · {b.pickup} → {b.destination}</div>
    </button>
  );
}

function DriverCard({ d, now, route }) {
  if (!d) return <div className="text-sm text-slate-500 p-6 text-center" data-testid="gps-no-selection">Seleziona un autista per vedere la scheda.</div>;
  const S = STATUS[d.status] || STATUS.offline, p = d.position;
  const row = (k, v, t) => <div className="flex justify-between gap-3 py-1.5 border-b border-white/5 text-sm"><span className="text-slate-400">{k}</span><span className="text-slate-100 text-right" data-testid={t}>{v}</span></div>;
  return (
    <div className="p-4 space-y-3" data-testid="gps-driver-card">
      <div>
        <div className="text-lg font-bold text-white">{d.driver_name}</div>
        <div className={`text-xs font-bold inline-flex items-center gap-1.5 ${S.text}`}><span className={`w-2 h-2 rounded-full ${S.dot}`} />{S.label}</div>
      </div>
      <div>
        {row("Veicolo", d.vehicle_name ? `${d.vehicle_name} · ${d.vehicle_plate}` : (d.vehicle_plate || "—"), "gps-card-vehicle")}
        {row("Turno", `${SHIFT[d.shift_type] || d.shift_type || "—"}${d.started_at ? ` · da ${hhmm(d.started_at)}` : ""}`)}
        {row("Ultima posizione", p ? `${hhmm(p.received_at)} (${ago(p.received_at, now)})` : "Nessuna posizione ricevuta", "gps-card-last")}
        {p && row("Precisione", p.accuracy != null ? `± ${Math.round(p.accuracy)} m` : "—")}
        {p && p.speed != null && row("Velocità", `${Math.round(p.speed * 3.6)} km/h`)}
        {d.status !== "not_started" && d.status !== "ended" && <BookingLink label="Servizio in corso" b={d.current_booking} testid="gps-card-current" />}
        {d.status !== "not_started" && d.status !== "ended" && <BookingLink label="Prossimo servizio" b={d.next_booking} testid="gps-card-next" />}
        {d.status !== "not_started" && d.status !== "ended" && <RouteInfo route={route} />}
      </div>
      {p && (
        <a href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`} target="_blank" rel="noreferrer" data-testid="gps-open-maps"
          className="h-10 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm inline-flex items-center justify-center gap-2 w-full">
          <Navigation size={15} />Apri su Google Maps
        </a>
      )}
      {d.status !== "live" && p && <div className="text-[11px] text-amber-300/80" data-testid="gps-stale-warning">Attenzione: posizione non in tempo reale, mostrata l'ultima ricevuta.</div>}
    </div>
  );
}

export default function GpsLive() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selId, setSelId] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [mapErr, setMapErr] = useState(null);
  const [route, setRoute] = useState(null);
  const mapEl = useRef(null), mapRef = useRef(null), gRef = useRef(null), markers = useRef({}), routeLayer = useRef(null), fitted = useRef(null);

  const load = async () => {
    try { setData((await api.get("/gps/live")).data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000), c = setInterval(() => setNow(Date.now()), 5000);
    return () => { clearInterval(t); clearInterval(c); };
  }, []);

  useEffect(() => {
    loadGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY).then((g) => {
      gRef.current = g;
      mapRef.current = new g.Map(mapEl.current, { center: { lat: 41.9028, lng: 12.4964 }, zoom: 11, styles: MAP_STYLE, disableDefaultUI: false,
        streetViewControl: false, mapTypeControl: true, backgroundColor: "#0B1220" });
      setNow(Date.now());
    }).catch((e) => setMapErr(e.message));
  }, []);

  const drivers = useMemo(() => (data?.drivers || []).filter(d =>
    (filter === "all" || d.status === filter) && (!q || (d.driver_name || "").toLowerCase().includes(q.toLowerCase()) || (d.vehicle_plate || "").toLowerCase().includes(q.toLowerCase()))), [data, q, filter]);
  const sel = (data?.drivers || []).find(d => d.driver_id === selId);

  useEffect(() => {
    const g = gRef.current, map = mapRef.current;
    if (!g || !map || !data) return;
    const seen = new Set();
    data.drivers.filter(d => d.position && STATUS[d.status]?.pin).forEach(d => {
      seen.add(d.driver_id);
      const pos = { lat: d.position.lat, lng: d.position.lng };
      const icon = carIcon(window.google.maps, STATUS[d.status].pin, d.position.heading, 46);
      const label = { text: `${(d.driver_name || "").split(" ")[0]} · ${d.vehicle_plate || ""}`, color: "#E0F2FE", fontSize: "11px", fontWeight: "700" };
      let m = markers.current[d.driver_id];
      if (!m) {
        m = new g.Marker({ map, position: pos, icon, label, title: `${d.driver_name || ""} · ${d.vehicle_plate || ""}`, optimized: false });
        m.addListener("click", () => setSelId(d.driver_id));
        markers.current[d.driver_id] = m;
      } else { m.setPosition(pos); m.setIcon(icon); m.setLabel(label); }
    });
    Object.keys(markers.current).forEach(id => { if (!seen.has(id)) { markers.current[id].setMap(null); delete markers.current[id]; } });
  }, [data, now]);

  const posAt = sel?.position?.received_at;
  useEffect(() => {
    if (!selId) { setRoute(null); return; }
    let off = false;
    api.get(`/gps/route/${selId}`).then(r => { if (!off) setRoute(r.data); }).catch(() => { if (!off) setRoute({ available: false, reason: "route_error" }); });
    return () => { off = true; };
  }, [selId, posAt, sel?.next_booking?.id]);

  useEffect(() => {
    const g = gRef.current, map = mapRef.current;
    if (routeLayer.current) { routeLayer.current.forEach(x => x.setMap(null)); routeLayer.current = null; }
    if (!g || !map || !route?.available) return;
    const path = decodePath(route.polyline);
    const line = new window.google.maps.Polyline({ map, path, strokeColor: "#34D399", strokeOpacity: 0.9, strokeWeight: 5, zIndex: 5 });
    const pin = new g.Marker({ map, position: route.end, title: `Ritiro ${route.booking?.booking_number || ""} · ${route.booking?.pickup || ""}`, zIndex: 6,
      label: { text: "R", color: "#052e16", fontWeight: "800", fontSize: "12px" },
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 11, fillColor: "#34D399", fillOpacity: 1, strokeColor: "#052e16", strokeWeight: 2 } });
    routeLayer.current = [line, pin];
    const fk = `${selId}:${route.booking?.id}`;
    if (fitted.current !== fk) {
      fitted.current = fk;
      const bounds = new window.google.maps.LatLngBounds(); path.forEach(p => bounds.extend(p));
      map.fitBounds(bounds, 60);
    }
  }, [route, selId]);

  const focus = (d) => {
    setSelId(d.driver_id);
    if (d.position && mapRef.current) { mapRef.current.panTo({ lat: d.position.lat, lng: d.position.lng }); mapRef.current.setZoom(15); }
  };
  const preselected = useRef(false);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("driver");
    if (preselected.current || !id || !data || !mapRef.current) return;
    const d = data.drivers.find(x => x.driver_id === id);
    if (d) { preselected.current = true; focus(d); }
  }, [data, now]);
  const c = data?.counts || {};

  return (
    <div className="space-y-5" data-testid="gps-live-page">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <MapPin size={36} className="text-sky-400 drop-shadow-[0_0_12px_rgba(56,189,248,0.6)]" />
          <div>
            <h1 className="text-4xl sm:text-5xl font-display font-extrabold text-white tracking-tight">GPS LIVE</h1>
            <div className="text-sm text-slate-400">Posizione in tempo reale dei driver in turno · aggiornamento automatico ogni 15 s</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Counter value={c.in_turno ?? 0} label="Driver in turno" dot="bg-emerald-400" testid="gps-count-inturno" />
          <Counter value={c.live ?? 0} label="Localizzati LIVE" dot="bg-sky-400" testid="gps-count-live" />
          <Counter value={c.offline ?? 0} label="Offline / non aggiornati" dot="bg-slate-500" testid="gps-count-offline" />
          <Counter value={c.denied ?? 0} label="Permesso negato" dot="bg-rose-500" testid="gps-count-denied" />
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr_300px] gap-4">
        <div className="rounded-2xl border border-sky-500/20 bg-[#060D19] p-3 space-y-2 xl:max-h-[620px] flex flex-col">
          <div className="text-xs font-bold tracking-[0.2em] text-sky-300 uppercase px-1">Autisti in turno</div>
          <div className="flex flex-col gap-2">
            <div className="relative"><Search size={14} className="absolute left-2.5 top-3 text-slate-500" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca autista o targa…" data-testid="gps-search"
                className="h-10 w-full rounded-lg bg-slate-950 border border-slate-800 text-sm text-slate-200 pl-8 pr-2 focus:border-sky-500/60 outline-none" /></div>
            <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="gps-filter"
              className="h-10 w-full rounded-lg bg-slate-950 border border-slate-800 text-xs text-slate-200 px-2">
              <option value="all">Tutti gli stati</option>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="overflow-y-auto flex-1 space-y-0.5 min-h-[120px]">
            {!data ? <div className="text-sm text-slate-500 p-4">Caricamento…</div>
              : drivers.length === 0 ? <div className="text-sm text-slate-500 p-4" data-testid="gps-empty">Nessun autista in turno oggi.</div>
              : drivers.map(d => <DriverRow key={d.driver_id} d={d} sel={d.driver_id === selId} onClick={() => focus(d)} now={now} />)}
          </div>
          <button type="button" onClick={load} className="text-[11px] text-slate-500 hover:text-sky-300 inline-flex items-center gap-1 px-1" data-testid="gps-refresh">
            <RefreshCw size={11} />Aggiornato {data ? hhmm(data.server_time) : "—"}
          </button>
        </div>
        <div className="rounded-2xl border border-sky-500/20 bg-[#0B1220] overflow-hidden min-h-[420px] xl:min-h-[620px] relative">
          <div ref={mapEl} className="absolute inset-0" data-testid="gps-map" />
          {mapErr && <div className="absolute inset-0 flex items-center justify-center text-sm text-rose-300 p-6 text-center" data-testid="gps-map-error">Mappa non disponibile: {mapErr}</div>}
        </div>
        <div className="rounded-2xl border border-sky-500/20 bg-[#060D19]"><DriverCard d={sel} now={now} route={route} /></div>
      </div>
      <div className="text-[11px] text-slate-500">LIVE = posizione ricevuta da meno di 60 s · Non aggiornata = 1–5 min · Offline = oltre 5 min. Viene conservata solo l'ultima posizione, cancellata 12 ore dopo la fine del turno. Con l'app chiusa o lo schermo bloccato il telefono sospende l'invio.</div>
    </div>
  );
}
